import { isNil, get, isEmpty, isEqual } from 'lodash';

/**
 * Checks if the project is shared or not.
 *
 * @param {object} projectObject - The project object.
 * @returns {boolean} True if the project is shared, false otherwise.
 * @throws {Error} If project is null or undefined.
 */
export const isShared = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'shared', false);
};

/**
 * Gets the project id.
 *
 * @param {object} projectObject - The project object.
 * @returns {string} The id for the project or empty string id not found.
 * @throws {Error} If project is null or undefined.
 */
export const getId = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, '_id', '');
};

/**
 * Gets the project owner.
 *
 * @param {object} projectObject - The project object.
 * @returns {string} The owner for the project or empty string id not found.
 * @throws {Error} If project is null or undefined.
 */
export const getOwner = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'owner', '');
};

/**
 * Get project name.
 *
 * @param {object} projectObject - The project object.
 * @returns {string} The name of the project or empty string id not found.
 * @throws {Error} If project is null or undefined.
 */
export const getName = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'name', '');
};

/**
 * Get project description.
 *
 * @param {object} projectObject - The project object.
 * @returns {string} The description of the project or empty string id not found.
 * @throws {Error} If project is null or undefined.
 */
export const getDescription = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'description', '');
};

/**
 * Get project bounding box.
 *
 * @param {object} projectObject - The project object.
 * @returns {Array} The bounding box for the project map or an empty array if it's not defined.
 * @throws {Error} If project is null or undefined.
 */
export const getBoundingBox = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'mapLocation.bbox', []);
};

/**
 * Get dtm file bounding box.
 *
 * @param {object} projectObject - The project object.
 * @returns {Array} The bounding box for the dtm file or an empty array if it's not defined.
 * @throws {Error} If project is null or undefined.
 */
export const getDTMBbox = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'dtmFileInfo.dtmBbox', []);
};

/**
 * Get project bounding box.
 *
 * @param {object} projectObject - The project object.
 * @returns {boolean} True if the 2 bound boxes are defined and equal.
 * @throws {Error} If project is null or undefined.
 */
export const isDTMbboxCorrect = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  const projectBbox = getBoundingBox(projectObject);
  const dtmBbox = getDTMBbox(projectObject);
  if (isEmpty(projectBbox) || isEmpty(dtmBbox)) {
    return false;
  }
  if (isEqual(projectBbox, dtmBbox)) {
    return true;
  }
  return false;
};

/**
 * Get project location.
 *
 * @param {object} projectObject - The project object.
 * @returns {object} The location of the project or an empty object if it's not defined.
 * @throws {Error} If project is null or undefined.
 */
export const getMapLocation = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'mapLocation', {});
};

/**
 * Checks if the project is deleted or not.
 *
 * @param {object} projectObject - The project object.
 * @returns {boolean} True if the project is deleted, false otherwise.
 * @throws {Error} If project is null or undefined.
 */
export const isDeleted = projectObject => {
  if (isNil(projectObject)) {
    throw new Error('project not defined');
  }
  return get(projectObject, 'deleted', '') !== 'no';
};
